import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Stack, Button, Select, Option, Textarea, FormControl, FormLabel, CircularProgress, Alert, Input } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { getGlobalPositions, upsertEmployerPosition, getEmployer } from '../../../apiconfig/api';
import { useSession } from '../../../context/SessionContext';
import { v4 as uuidv4 } from 'uuid';

interface WizardStep {
  id: number;
  title: string;
  description: string;
}

const steps: WizardStep[] = [
  {
    id: 1,
    title: "What industry role best describes this position?",
    description: "Select the role that most closely matches this position."
  },
  {
    id: 2,
    title: "Is this position full-time or part-time?",
    description: "Choose the employment type that best fits this role. You can select both if the position can accommodate either."
  },
  {
    id: 3,
    title: "Describe the responsibilities and ideal person",
    description: "Provide a detailed description of the position and what you're looking for."
  },
  {
    id: 4,
    title: "Create your position",
    description: "Review your position details and create the post."
  }
];

const CreatePosition: React.FC = () => {
  const navigate = useNavigate();
  const { sessionEmployerId } = useSession();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [selectedRole, setSelectedRole] = useState('');
  const [isPartTime, setIsPartTime] = useState(false);
  const [isFullTime, setIsFullTime] = useState(false);
  const [description, setDescription] = useState('');
  
  // Global positions state
  const [globalPositions, setGlobalPositions] = useState<any[]>([]);
  const [loadingGlobalPositions, setLoadingGlobalPositions] = useState(true);

  // Fetch global positions
  useEffect(() => {
    const fetchGlobalPositions = async () => {
      try {
        setLoadingGlobalPositions(true);
        const response = await getGlobalPositions();
        setGlobalPositions(response.data?.globalPositions || []);
      } catch (err) {
        console.error("Error fetching global positions:", err);
        setError("Failed to load available roles. Please try again.");
      } finally {
        setLoadingGlobalPositions(false);
      }
    };

    fetchGlobalPositions();
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreatePosition = async () => {
    if (!sessionEmployerId || !selectedRole || (!isFullTime && !isPartTime) || !description) {
      setError("Please complete all required fields.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const id = uuidv4();

      // Find the selected global position
      const selectedGlobalPosition = globalPositions.find(pos => pos.id === selectedRole);
      if (!selectedGlobalPosition) {
        throw new Error("Selected role not found");
      }

      // Get employer data to get the slug
      const employerResponse = await getEmployer(sessionEmployerId);
      const employerSlug = employerResponse.data.employer.slug;

      // Create the position payload
      const payload = {
        displayNameOverride: selectedGlobalPosition.displayName,
        slugOverride: selectedGlobalPosition.slug,
        positionId: selectedRole,
        isPartTime: isPartTime,
        isFullTime: isFullTime,
        isHourly: false, // Always false for CreatePosition
        isSalary: false, // Always false for CreatePosition
        minHourlyCents: null, // Always null for CreatePosition
        maxHourlyCents: null, // Always null for CreatePosition
        minSalaryCents: null, // Always null for CreatePosition
        maxSalaryCents: null, // Always null for CreatePosition
        description: description,
        isActive: true
      };

      // Create the position
      const response = await upsertEmployerPosition(sessionEmployerId, id, payload);
      
      // Navigate to edit page using employer slug
      navigate(`/hiring/${employerSlug}/${payload.slugOverride}/edit`);
      
    } catch (err: any) {
      console.error("Error creating position:", err);
      setError(err.message || "Failed to create position. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormControl>
            <Select
              value={selectedRole}
              onChange={(_, value) => setSelectedRole(value || '')}
              placeholder={loadingGlobalPositions ? "Loading roles..." : "Choose a role"}
              disabled={loadingGlobalPositions}
              sx={{
                height: '56px',
                backgroundColor: '#ECEFF3',
                borderRadius: '12px',
                border: 'none',
                fontSize: '1.125rem',
                color: '#1A1B1D',
                '--Input-focusedHighlight': 'transparent !important',
                '&::placeholder': {
                  color: '#7B7F85 !important',
                  opacity: 1,
                },
                '&:hover': {
                  backgroundColor: '#ECEFF3',
                },
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  backgroundColor: '#ECEFF3',
                },
              }}
            >
              {globalPositions.map((position) => (
                <Option key={position.id} value={position.id}>
                  {position.displayName}
                </Option>
              ))}
            </Select>
          </FormControl>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
              Employment Type
            </Typography>
            <Stack direction="row" spacing={2}>
              <Box
                onClick={() => setIsFullTime(!isFullTime)}
                sx={{
                  flex: 1,
                  height: '120px',
                  padding: '24px',
                  borderRadius: '12px',
                  backgroundColor: isFullTime ? '#F0F2F5' : '#F8F9FA',
                  border: isFullTime ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#2e2d7e',
                    backgroundColor: isFullTime ? '#E8EAED' : '#F0F2F5',
                  },
                }}
              >
                <Typography level="h4" sx={{ fontWeight: 600, mb: 1, color: '#1A1B1D' }}>
                  Full-time
                </Typography>
                <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.3 }}>
                  Permanent positions with 40+ hours per week, typically with benefits and long-term commitment.
                </Typography>
              </Box>
              
              <Box
                onClick={() => setIsPartTime(!isPartTime)}
                sx={{
                  flex: 1,
                  height: '120px',
                  padding: '24px',
                  borderRadius: '12px',
                  backgroundColor: isPartTime ? '#F0F2F5' : '#F8F9FA',
                  border: isPartTime ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#2e2d7e',
                    backgroundColor: isPartTime ? '#E8EAED' : '#F0F2F5',
                  },
                }}
              >
                <Typography level="h4" sx={{ fontWeight: 600, mb: 1, color: '#1A1B1D' }}>
                  Part-time
                </Typography>
                <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.3 }}>
                  Flexible positions with fewer hours, often suitable for students or those seeking work-life balance.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        );

      case 3:
        return (
          <FormControl>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the responsibilities, requirements, and ideal person for this position..."
              minRows={6}
              sx={{
                backgroundColor: '#ECEFF3',
                borderRadius: '12px',
                border: 'none',
                fontSize: '1.125rem',
                color: '#1A1B1D',
                '--Textarea-focusedHighlight': 'transparent !important',
                '&::placeholder': {
                  color: '#7B7F85 !important',
                  opacity: 1,
                },
                '&:hover': {
                  backgroundColor: '#ECEFF3',
                },
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  backgroundColor: '#ECEFF3',
                },
              }}
            />
            <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
              {description.length} characters (recommended: 100-500)
            </Typography>
          </FormControl>
        );

      case 4:
        const selectedGlobalPosition = globalPositions.find(pos => pos.id === selectedRole);
        return (
          <Stack spacing={3}>
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Review your position
            </Typography>
            
            <Box sx={{ p: 3, backgroundColor: '#F8F9FA', borderRadius: '12px', border: '2px solid #E5E7EB' }}>
              <Stack spacing={2}>
                <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
                  {selectedGlobalPosition?.displayName}
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                  {isFullTime && isPartTime 
                    ? 'Full-time or Part-time' 
                    : isFullTime 
                      ? 'Full-time' 
                      : isPartTime 
                        ? 'Part-time' 
                        : 'Employment type not selected'
                  } position
                </Typography>
                <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.4 }}>
                  {description}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedRole !== '';
      case 2:
        return (isFullTime || isPartTime);
      case 3:
        return description.trim().length > 0;
      default:
        return true;
    }
  };

  const isLastStep = currentStep === steps.length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: 4 }}>
      <Container maxWidth="md">
        <Stack spacing={4}>
          {/* Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography level="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Create New Position
            </Typography>
            <Typography level="body-lg" sx={{ color: '#7B7F85' }}>
              Step {currentStep} of {steps.length}
            </Typography>
          </Box>

          {/* Progress indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Stack direction="row" spacing={1}>
              {steps.map((step) => (
                <Box
                  key={step.id}
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: step.id <= currentStep ? '#2e2d7e' : '#E0E3E7',
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Error display */}
          {error && (
            <Alert color="danger" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step content */}
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '16px', 
            p: 4, 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' 
          }}>
            <Stack spacing={4}>
              {/* Step title and description */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography level="h3" sx={{ fontWeight: 600, mb: 2 }}>
                  {steps[currentStep - 1].title}
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                  {steps[currentStep - 1].description}
                </Typography>
              </Box>

              {/* Step content */}
              {renderStepContent()}

              {/* Navigation buttons */}
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  sx={{
                    borderColor: '#2e2d7e',
                    color: '#2e2d7e',
                    '&:hover': {
                      borderColor: '#1a1a5e',
                      color: '#1a1a5e',
                    },
                  }}
                >
                  Back
                </Button>

                <Button
                  variant="solid"
                  onClick={isLastStep ? handleCreatePosition : handleNext}
                  disabled={!canProceed() || loading}
                  sx={{
                    backgroundColor: '#2e2d7e',
                    '&:hover': { backgroundColor: '#1e1d6e' },
                    '&:disabled': { backgroundColor: '#7B7F85' }
                  }}
                >
                                     {loading ? (
                     <CircularProgress size="sm" color="neutral" />
                   ) : isLastStep ? (
                    'Create Position'
                  ) : (
                    'Next'
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default CreatePosition; 